/**
 * useOrgRole — single source of truth for "what can this user do in this org"
 * on the client.
 *
 * Returns:
 *   {
 *     role,        // 'owner' | 'admin' | 'manager' | 'member' | null
 *     isOwner,     // boolean
 *     isMember,    // boolean (any role, including owner)
 *     can: { ... }, // camelCased booleans, one per permission in PERMISSIONS
 *   }
 *
 * Server is the authoritative gate (server/api-util/orgPermissions.js).
 * This hook exists purely so the UI can hide/disable controls the server
 * would reject anyway — never use it as a security boundary on its own.
 *
 * Usage:
 *   const { can, role } = useOrgRole(orgId);
 *   if (can.inviteTeam) { ... }
 *   <Can permission="EDIT_ANY_SHOW">...</Can>
 *
 * Spec: UPDATE.md §3.6.5 (permission matrix).
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { PERMISSIONS, roleHasPermission } from '../util/orgRoles';
import { getUserOrgRole, isOrgOwner, isOrgMember } from '../util/orgMembership';

// Permission keys → camelCase property names exposed on `can`.
// Mapping kept explicit (rather than auto-derived) so renames in either
// direction stay greppable and don't silently break.
const PERMISSION_TO_CAN_KEY = Object.freeze({
  VIEW_DASHBOARD: 'viewDashboard',
  CREATE_SHOWS: 'createShows',
  EDIT_ANY_SHOW: 'editAnyShow',
  DELETE_SHOWS: 'deleteShows',
  ASSIGN_CREATORS: 'assignCreators',
  PUBLISH_EPISODES: 'publishEpisodes',
  CREATE_RESEARCH: 'createResearch',
  VIEW_INSIGHTS: 'viewInsights',
  VIEW_RENTED_AUDIENCE: 'viewRentedAudience',
  SEND_NEWSLETTERS: 'sendNewsletters',
  INVITE_TEAM: 'inviteTeam',
  REMOVE_TEAM: 'removeTeam',
  CHANGE_ROLES: 'changeRoles',
  VIEW_BILLING: 'viewBilling',
  MANAGE_SUBSCRIPTION: 'manageSubscription',
  DELETE_ORG: 'deleteOrg',
});

const ALL_FALSE_CAN = Object.freeze(
  Object.values(PERMISSION_TO_CAN_KEY).reduce(
    (acc, key) => ({ ...acc, [key]: false }),
    {}
  )
);

const buildCan = role => {
  if (!role) return ALL_FALSE_CAN;
  const out = {};
  for (const permKey of Object.keys(PERMISSIONS)) {
    const canKey = PERMISSION_TO_CAN_KEY[permKey];
    if (!canKey) continue; // permission added to matrix without a key map — skip
    out[canKey] = roleHasPermission(role, permKey);
  }
  return Object.freeze(out);
};

/**
 * @param {string|null} orgId — the org the user is currently acting in.
 *   Pass null/undefined for non-org pages; the hook returns an "outside any
 *   org" result (role=null, all `can` flags false).
 */
export const useOrgRole = orgId => {
  const currentUser = useSelector(state => state.user.currentUser);

  return useMemo(() => {
    if (!orgId || !currentUser) {
      return {
        role: null,
        isOwner: false,
        isMember: false,
        can: ALL_FALSE_CAN,
      };
    }
    const role = getUserOrgRole(currentUser, orgId);
    return {
      role,
      isOwner: isOrgOwner(currentUser, orgId),
      isMember: isOrgMember(currentUser, orgId),
      can: buildCan(role),
    };
  }, [currentUser, orgId]);
};

export default useOrgRole;

// Exported for the <Can> component so it can map a permission key arg to
// the same boolean. Keep them in lockstep.
export { PERMISSION_TO_CAN_KEY };
